import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Linkedin, Twitter, Facebook, ArrowRight } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_c69a6c1b-5824-475e-9621-3a91f4bdc21c/artifacts/hu5ydhul_ChatGPT%20Image%20Jan%2016%2C%202026%2C%2011_17_05%20AM.png";

const footerLinks = {
  company: [
    { name: 'About Us', path: '/about' },
    { name: 'Our Services', path: '/services' },
    { name: 'Blog', path: '/blog' },
    { name: 'Careers', path: '/careers' },
    { name: 'Contact Us', path: '/contact' },
  ],
  services: [
    { name: 'Employee Background Verification', path: '/services' },
    { name: 'Insurance Verification', path: '/services' },
    { name: 'Identity & KYC Checks', path: '/services' },
    { name: 'Criminal Record Checks', path: '/services' },
  ],
  industries: [
    { name: 'Fintech & BFSI', path: '/services' },
    { name: 'Insurance Companies', path: '/services' },
    { name: 'IT & SaaS', path: '/services' },
    { name: 'Healthcare', path: '/services' },
  ],
};

export const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-slate-900 text-white" data-testid="footer">
      {/* CTA Section */}
      <div className="border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="text-2xl font-bold mb-2">Ready to Start Verifying?</h3>
              <p className="text-slate-400">Get in touch with our team for a customized solution.</p>
            </div>
            <Link 
              to="/contact"
              className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white px-8 py-3 rounded-md font-medium transition-colors"
              data-testid="footer-cta-btn"
            >
              Contact Our Team
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12">
          {/* Company Info */}
          <div className="lg:col-span-2">
            <img 
              src={LOGO_URL} 
              alt="Aarambh India Service" 
              className="h-16 w-auto mb-6 brightness-0 invert"
            />
            <p className="text-slate-400 mb-6 leading-relaxed">
              A trusted background verification and risk assessment organization, 
              committed to delivering accurate, compliant, and timely verification 
              solutions across India.
            </p>
            <div className="space-y-3">
              <a 
                href="mailto:info@aisverify.com" 
                className="flex items-center gap-3 text-slate-400 hover:text-emerald-400 transition-colors"
              >
                <Mail className="w-5 h-5" />
                info@aisverify.com
              </a>
              <a 
                href="tel:7566602484" 
                className="flex items-center gap-3 text-slate-400 hover:text-emerald-400 transition-colors"
              >
                <Phone className="w-5 h-5" />
                +91 7566602484, +91 9319559556
              </a>
              <div className="flex items-start gap-3 text-slate-400">
                <MapPin className="w-5 h-5 flex-shrink-0 mt-0.5" />
                <span>Plot No. 39, 40, New Chouksey Nagar, Bhopal, Near MK Ponda College, 462038, Madhya Pradesh</span>
              </div>
            </div>
          </div>

          {/* Company Links */}
          <div>
            <h4 className="font-semibold text-lg mb-4">Company</h4>
            <ul className="space-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.name}>
                  <Link 
                    to={link.path}
                    className="text-slate-400 hover:text-white transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services Links */}
          <div>
            <h4 className="font-semibold text-lg mb-4">Services</h4>
            <ul className="space-y-3">
              {footerLinks.services.map((link) => (
                <li key={link.name}>
                  <Link 
                    to={link.path}
                    className="text-slate-400 hover:text-white transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Industries Links */}
          <div>
            <h4 className="font-semibold text-lg mb-4">Industries</h4>
            <ul className="space-y-3">
              {footerLinks.industries.map((link) => (
                <li key={link.name}>
                  <Link 
                    to={link.path}
                    className="text-slate-400 hover:text-white transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="text-center md:text-left">
              <p className="text-slate-400 text-sm">
                © 2020 Aarambh India Service Pvt. Ltd. All rights reserved.
              </p>
              <p className="text-slate-500 text-xs mt-1">
                CIN: U74920MP2020PTC051111 | ISO 9001:2015 Certified
              </p>
            </div>
            <div className="flex items-center gap-4">
              <a 
                href="https://linkedin.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center hover:bg-emerald-500 transition-colors"
                data-testid="social-linkedin"
              >
                <Linkedin className="w-5 h-5" />
              </a>
              <a 
                href="https://twitter.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center hover:bg-emerald-500 transition-colors"
                data-testid="social-twitter"
              >
                <Twitter className="w-5 h-5" />
              </a>
              <a 
                href="https://facebook.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center hover:bg-emerald-500 transition-colors"
                data-testid="social-facebook"
              >
                <Facebook className="w-5 h-5" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
